import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminGrowthSnapshotStaticScreenDeleteAction,
  adminGrowthSnapshotStaticScreensListAction,
  adminGrowthSnapshotsListAction
} from "../Action/action";

function formatScreenKey(value) {
  const s = String(value || "").replace(/_/g, " ").trim();
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function StaticScreensList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState(searchParams.get("snapshot_id") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotsListAction({ page: 1, per_page: 100 });
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setSnapshots(list);
        if (!snapshotId && list.length) setSnapshotId(String(list[0].id));
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load snapshots.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!snapshotId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setSearchParams({ snapshot_id: snapshotId }, { replace: true });
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotStaticScreensListAction(snapshotId);
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || "Failed to load static screens.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(e?.response?.data?.message || e?.message || "Failed to load.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [snapshotId, reloadNonce, setSearchParams]);

  const onDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminGrowthSnapshotStaticScreenDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Deleted", { toastId: "gs-st-del" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", {
        toastId: "gs-st-del"
      });
    } finally {
      setDeleting(false);
    }
  };

  const addPath = snapshotId
    ? `/reflections-screens/static-screens/new?snapshot_id=${snapshotId}`
    : "/reflections-screens/static-screens/new";

  return (
    <div className="page-content content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">{error}</div>
        </div>
      ) : null}

      <div className="page-header-row toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <select
            className="form-control"
            style={{ maxWidth: 280 }}
            value={snapshotId}
            onChange={(e) => setSnapshotId(e.target.value)}
          >
            <option value="">Select growth snapshot…</option>
            {snapshots.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.title || `Snapshot #${s.id}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/reflections-screens" className="btn btn-outline btn-sm">
            ← Back
          </Link>
          <Link className="btn btn-primary" to={addPath}>
            + Add static screen
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            Static screens
          </h2>
          <span className="small-label">{rows.length} row(s)</span>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Screen key</th>
                <th>Section</th>
                <th>Heading</th>
                <th>Button</th>
                <th>Status</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>Loading…</td>
                </tr>
              ) : !snapshotId ? (
                <tr>
                  <td colSpan={6}>Select a growth snapshot.</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>No screens yet. Click “+ Add static screen”.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {formatScreenKey(row.screen_key)}
                    </td>
                    <td>
                      {row.section_id != null
                        ? `${row.section_title || `Section #${row.section_id}`}${
                            row.section_phase ? ` · ${row.section_phase}` : ""
                          }`
                        : "Snapshot-level"}
                    </td>
                    <td>{row.heading || "—"}</td>
                    <td>{row.primary_button_label || "—"}</td>
                    <td>
                      <span className={Number(row.status) === 1 ? "badge badge-success" : "badge"}>
                        {Number(row.status) === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link className="action-link" to={`/reflections-screens/static-screens/${row.id}/edit`}>
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          style={{ background: "transparent", border: "none", padding: 0 }}
                          onClick={() => {
                            setDeleteRow(row);
                            setDeleteOpen(true);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        title="Confirm Delete"
        itemType="static screen"
        itemName={deleteRow ? deleteRow.screen_key : ""}
        confirmDisabled={deleting}
        onCancel={() => {
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={onDelete}
      />
    </div>
  );
}
